/**
 * TokenManager
 * Handles token refresh coordination across multiple browser tabs
 *
 * Key Features:
 * - Lock-based coordination to prevent race conditions
 * - Timeout handling for stale locks
 * - Tab-specific IDs for ownership tracking
 * - Automatic cleanup and retry logic
 * - Namespace-based isolation so CRM and portal can share the class
 *   without colliding on storage keys
 */

import {
	getWorkdItem,
	getWorkdKey,
	removeWorkdItem,
	setWorkdItem,
} from '../storage/workdStorage';
import { useAuthStore } from '../stores';

export interface LockData {
	timestamp: number;
	tabId: string;
}

/**
 * Configuration for TokenManager.
 *
 * Allows callers to customize the storage namespace and the mechanism
 * used to read/write the tab ID. The CRM uses the default ('auth'
 * namespace, useAuthStore for tab ID). The portal passes its own
 * namespace and tab ID callbacks so it doesn't collide with CRM keys.
 */
export interface TokenManagerConfig {
	/**
	 * Storage namespace used for lock and auth state keys.
	 * Defaults to 'auth' (producing keys like workd:auth:refresh_lock).
	 */
	namespace?: string;

	/**
	 * Read the current tab ID. Called once during construction.
	 * Defaults to reading from useAuthStore.
	 */
	getTabId?: () => string | null;

	/**
	 * Persist a newly generated tab ID.
	 * Defaults to writing to useAuthStore.
	 */
	setTabId?: (tabId: string) => void;

	/**
	 * Extract the access token from a storage event's newValue string.
	 * Called when another tab updates the auth state key in localStorage.
	 * Defaults to parsing Zustand's persist format and reading `.accessToken`.
	 */
	parseTokenFromStorageEvent?: (newValue: string) => string | null;
}

export class TokenManager {
	/**
	 * How long a lock may go without a heartbeat before another tab may take it
	 * over. PD-13055: this used to be the total budget for the whole refresh, so
	 * a refresh slower than 5 seconds — an ordinary occurrence on a bad mobile
	 * connection — looked like a crashed tab and a second tab would start a
	 * competing refresh. The holder now heartbeats while it works (see
	 * LOCK_HEARTBEAT_INTERVAL), so this is a liveness timeout rather than a
	 * deadline, and an expired lock genuinely means the holder is gone.
	 */
	private static readonly LOCK_TIMEOUT = 5000;

	/**
	 * Cadence at which the lock holder refreshes its timestamp. Must be
	 * comfortably below LOCK_TIMEOUT so a live holder is never judged stale.
	 */
	private static readonly LOCK_HEARTBEAT_INTERVAL = 1500;

	/**
	 * How long a waiting tab will wait for the holder to publish a new token.
	 * Generous, because the waiter also gives up early when the lock is released
	 * or goes stale — it no longer relies on this timeout as its only exit.
	 */
	private static readonly STORAGE_EVENT_TIMEOUT = 30000;

	/** How often a waiting tab re-reads the lock to detect a dead holder. */
	private static readonly WAIT_POLL_INTERVAL = 500;

	/**
	 * PD-13055: coordinateRefresh used to recurse on every failed wait with no
	 * limit. Two tabs that kept failing could ping-pong until the stack blew or
	 * the browser tab locked up. Three attempts is enough to ride out one stale
	 * lock and one lost storage event.
	 */
	private static readonly MAX_COORDINATION_ATTEMPTS = 3;

	private readonly lockKey: string;
	private readonly authStateKey: string;
	private readonly namespace: string;
	private readonly parseTokenFromStorageEvent: (
		newValue: string,
	) => string | null;

	private refreshPromise: Promise<string | null> | null = null;
	private readonly tabId: string;
	private storageListeners: Array<(event: StorageEvent) => void> = [];
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

	constructor(config?: TokenManagerConfig) {
		this.namespace = config?.namespace ?? 'auth';
		this.lockKey = getWorkdKey(this.namespace, 'refresh_lock');
		this.authStateKey = getWorkdKey(this.namespace, 'state');

		this.parseTokenFromStorageEvent =
			config?.parseTokenFromStorageEvent ?? TokenManager.defaultParseToken;

		this.tabId = this.getOrCreateTabId(
			config?.getTabId ?? (() => useAuthStore.getState().tabId),
			config?.setTabId ??
				((id: string) => useAuthStore.getState().setTabId(id)),
		);
	}

	/**
	 * Default token parser for CRM auth — reads Zustand persist format.
	 */
	private static defaultParseToken(newValue: string): string | null {
		try {
			const parsed = JSON.parse(newValue);
			const authState = parsed.state ?? parsed;
			return authState.accessToken ?? null;
		} catch {
			return null;
		}
	}

	/**
	 * Get or create a unique tab ID for this session
	 */
	private getOrCreateTabId(
		getTabId: () => string | null,
		setTabId: (tabId: string) => void,
	): string {
		let tabId = getTabId();

		if (tabId == null) {
			tabId = `tab_${Date.now()}_${Math.random()}`;
			setTabId(tabId);
		}

		return tabId;
	}

	/**
	 * Try to acquire the refresh lock
	 * Returns true if lock was acquired, false if another tab holds it
	 */
	private tryAcquireLock(): boolean {
		const lockData = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

		if (lockData != null && !TokenManager.isLockStale(lockData)) {
			// Valid lock held by another tab
			return false;
		}

		if (lockData != null) {
			console.debug('[TokenManager] Stale lock detected, taking over');
		}

		return this.acquireLock();
	}

	/**
	 * Decide whether a lock may be taken over.
	 *
	 * PD-13055: the old check was a bare `age > LOCK_TIMEOUT`, which silently
	 * treats a corrupt lock as live forever. A `timestamp` of NaN (a truncated
	 * write, a hand-edited value, an older serialization) makes every numeric
	 * comparison false, so no tab could ever reclaim the lock and token refresh
	 * stopped working for that browser profile until storage was cleared. A
	 * timestamp in the future — a clock adjustment between tabs — had the same
	 * effect. Anything that is not a sane, non-future, recent timestamp is now
	 * treated as stale, which fails toward progress rather than deadlock.
	 */
	public static isLockStale(lockData: LockData): boolean {
		if (
			typeof lockData.timestamp !== 'number' ||
			!Number.isFinite(lockData.timestamp)
		) {
			return true;
		}

		const age = Date.now() - lockData.timestamp;

		// Negative age means the lock claims to have been taken in the future.
		return age < 0 || age > TokenManager.LOCK_TIMEOUT;
	}

	/**
	 * Acquire the refresh lock and confirm we actually won it.
	 *
	 * PD-13055: localStorage has no compare-and-swap, so the old read-then-write
	 * sequence let two tabs that observed the same stale lock both "acquire" it
	 * and both run a refresh — the exact race the lock exists to prevent. Writes
	 * are last-writer-wins and immediately visible to a subsequent read in the
	 * same tab, so writing and then reading back narrows the window to the gap
	 * between our own write and read: whichever tab wrote last reads its own
	 * tabId and proceeds, the other reads a foreign tabId and backs off. This is
	 * not a true mutex, but it removes the common case; a duplicate refresh
	 * remains merely wasteful rather than incorrect, because the auth server
	 * accepts the refresh token either way.
	 */
	private acquireLock(): boolean {
		const lockData: LockData = {
			timestamp: Date.now(),
			tabId: this.tabId,
		};

		setWorkdItem(this.namespace, 'refresh_lock', lockData);

		const readBack = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

		if (readBack == null || readBack.tabId !== this.tabId) {
			console.debug('[TokenManager] Lost the acquire race, backing off');

			return false;
		}

		this.startHeartbeat();

		return true;
	}

	/**
	 * Keep our lock's timestamp fresh while the refresh is in flight so other
	 * tabs do not mistake a slow network for a dead tab.
	 */
	private startHeartbeat(): void {
		this.stopHeartbeat();

		if (typeof setInterval !== 'function') {
			return;
		}

		this.heartbeatTimer = setInterval(() => {
			const lockData = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

			// Only heartbeat a lock we still own — if another tab took over, stop
			// rather than stomping on its claim.
			if (lockData != null && lockData.tabId === this.tabId) {
				setWorkdItem(this.namespace, 'refresh_lock', {
					timestamp: Date.now(),
					tabId: this.tabId,
				});
			} else {
				this.stopHeartbeat();
			}
		}, TokenManager.LOCK_HEARTBEAT_INTERVAL);
	}

	private stopHeartbeat(): void {
		if (this.heartbeatTimer != null) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
	}

	/**
	 * Release the refresh lock (only if we own it)
	 */
	private releaseLock(): void {
		this.stopHeartbeat();

		const lockData = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

		if (lockData != null) {
			// Only release if we own the lock
			if (lockData.tabId === this.tabId) {
				removeWorkdItem(this.namespace, 'refresh_lock');
				console.debug('[TokenManager] Released lock');
			}
		}
	}

	/**
	 * Wait for another tab to complete the refresh
	 * Returns the new token or rejects on timeout/failure
	 */
	private waitForRefresh(): Promise<string | null> {
		if (typeof window === 'undefined') {
			throw new Error('Token refresh timeout - no coordination available');
		}

		return new Promise((resolve, reject) => {
			let resolved = false;
			let timeoutId: NodeJS.Timeout;
			let pollId: ReturnType<typeof setInterval> | null = null;

			// Listen for auth_state update (which contains the access token)
			const tokenHandler = (e: StorageEvent) => {
				if (e.key === this.authStateKey && e.newValue != null && !resolved) {
					const token = this.parseTokenFromStorageEvent(e.newValue);

					if (token != null) {
						resolved = true;
						cleanup();
						console.debug('[TokenManager] Token updated by another tab');
						resolve(token);
					}
				}
			};

			// Listen for lock release without token update (failure case)
			const lockHandler = (e: StorageEvent) => {
				if (e.key === this.lockKey && e.newValue == null && !resolved) {
					resolved = true;
					cleanup();
					console.debug(
						'[TokenManager] Lock released without token update, will retry',
					);
					reject(new Error('Token refresh failed in another tab'));
				}
			};

			const cleanup = () => {
				window.removeEventListener('storage', tokenHandler);
				window.removeEventListener('storage', lockHandler);

				if (timeoutId != null) {
					clearTimeout(timeoutId);
				}

				if (pollId != null) {
					clearInterval(pollId);
					pollId = null;
				}

				// Remove from tracking
				const tokenIndex = this.storageListeners.indexOf(tokenHandler);

				if (tokenIndex > -1) {
					this.storageListeners.splice(tokenIndex, 1);
				}

				const lockIndex = this.storageListeners.indexOf(lockHandler);

				if (lockIndex > -1) {
					this.storageListeners.splice(lockIndex, 1);
				}
			};

			// Track listeners for cleanup
			this.storageListeners.push(tokenHandler, lockHandler);

			window.addEventListener('storage', tokenHandler);
			window.addEventListener('storage', lockHandler);

			// PD-13055: poll the lock as well as listening for storage events.
			// A `storage` event is only delivered to OTHER documents and is not
			// guaranteed — a tab that is discarded, crashes, or is closed
			// mid-refresh never fires the release event, so an event-only waiter
			// sat for the whole timeout before retrying. Re-reading the lock lets
			// us notice a dead holder within WAIT_POLL_INTERVAL instead.
			pollId = setInterval(() => {
				if (resolved) {
					return;
				}

				const lockData = getWorkdItem<LockData>(this.namespace, 'refresh_lock');

				if (lockData == null || TokenManager.isLockStale(lockData)) {
					resolved = true;
					cleanup();
					console.debug(
						'[TokenManager] Lock holder is gone, will retry the refresh',
					);
					reject(new Error('Token refresh abandoned by the holding tab'));
				}
			}, TokenManager.WAIT_POLL_INTERVAL);

			// Timeout if no response within window
			timeoutId = setTimeout(() => {
				if (!resolved) {
					resolved = true;
					cleanup();
					console.debug('[TokenManager] Wait timeout, will retry');
					reject(new Error('Token refresh timeout'));
				}
			}, TokenManager.STORAGE_EVENT_TIMEOUT);
		});
	}

	/**
	 * Coordinate token refresh across tabs
	 * Ensures only one tab refreshes at a time, others wait for result
	 */
	async coordinateRefresh(
		performRefresh: () => Promise<string | null>,
		attempt = 1,
	): Promise<string | null> {
		// If refresh already in progress in this tab, wait for it
		if (this.refreshPromise != null) {
			console.debug('[TokenManager] Refresh already in progress in this tab');

			return this.refreshPromise;
		}

		// Try to acquire lock
		if (!this.tryAcquireLock()) {
			// Another tab has the lock, wait for it to complete
			console.debug('[TokenManager] Another tab is refreshing, waiting...');

			try {
				const newToken = await this.waitForRefresh();

				return newToken;
			} catch (error) {
				// PD-13055: retry, but a bounded number of times. This used to
				// recurse unconditionally, so two tabs failing in lockstep could
				// spin indefinitely. Surfacing the failure lets the caller treat it
				// as a failed refresh and send the user back through login, which
				// is the correct end state after this many attempts.
				if (attempt >= TokenManager.MAX_COORDINATION_ATTEMPTS) {
					console.debug(
						`[TokenManager] Giving up after ${attempt} coordination attempts`,
					);

					throw error instanceof Error
						? error
						: new Error('Token refresh coordination failed');
				}

				console.debug(
					`[TokenManager] Waiting failed, retrying (attempt ${attempt + 1} of ${TokenManager.MAX_COORDINATION_ATTEMPTS})...`,
					error,
				);

				return this.coordinateRefresh(performRefresh, attempt + 1);
			}
		}

		// We have the lock, perform refresh
		console.debug('[TokenManager] Acquired lock, performing refresh');
		this.refreshPromise = this.performRefreshWithCleanup(performRefresh);

		return this.refreshPromise;
	}

	/**
	 * Perform the actual token refresh with proper cleanup
	 */
	private async performRefreshWithCleanup(
		performRefresh: () => Promise<string | null>,
	): Promise<string | null> {
		try {
			const newToken = await performRefresh();

			// The caller is responsible for persisting the new token to storage.
			// Other tabs listen for the auth state storage event and extract the new token.
			console.debug('[TokenManager] Refresh successful');

			this.releaseLock();

			return newToken;
		} catch (error) {
			console.debug('[TokenManager] Refresh failed:', error);
			this.releaseLock();

			throw error;
		} finally {
			this.refreshPromise = null;
		}
	}

	/**
	 * Cleanup method to remove storage listeners
	 */
	cleanup(): void {
		if (typeof window !== 'undefined') {
			for (const listener of this.storageListeners) {
				window.removeEventListener('storage', listener);
			}

			this.storageListeners = [];
		}

		// releaseLock() stops the heartbeat, but stop it here too so a cleanup
		// that races a lock takeover can never leave an interval running.
		this.stopHeartbeat();
		this.releaseLock();
	}
}
